import {getCbarMultiSelectIdsFromElement, isCbarMultiSelect} from '../cbar-select-bridge';

type CbarMultiSelectElement = HTMLElement & {
    items: Array<{id: string; value: string}>;
    value: Array<{id: string; value: string} | string>;
    name?: string;
    _internals?: ElementInternals;
};

const CBAR_MULTI_SELECT_SUBMIT_NAME_ATTR = 'data-cbar-multi-select-submit-name';
const CBAR_MULTI_SELECT_HIDDEN_ATTR = 'data-cbar-multi-select-field';
const CBAR_MULTI_SELECT_HYDRATED_ATTR = 'data-cbar-multi-select-hydrated';
const CBAR_MULTI_SELECT_VALUE_SYNC_ATTR = 'data-cbar-multi-select-value-sync';
const CBAR_MULTI_SELECT_ITEMS_MANAGED_ATTR = 'data-cbar-multi-select-items-managed';
const CBAR_MULTI_SELECT_INITIAL_ITEMS_ATTR = 'data-cbar-multi-select-initial-items';

type LitElementLike = HTMLElement & {updateComplete?: Promise<boolean>};

function escapeSelectorValue(value: string): string {
    if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
        return CSS.escape(value);
    }

    return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

function normalizeMultiSelectItemEntry(entry: unknown): {id: string; value: string} | null {
    if (typeof entry === 'object' && entry !== null) {
        const record = entry as Record<string, unknown>;
        const id = String(record.id ?? '');
        if (id === '') {
            return null;
        }

        return {
            id,
            value: String(record.value ?? record.title ?? id),
        };
    }

    const text = String(entry ?? '').trim();
    if (text === '') {
        return null;
    }

    return {id: text, value: text};
}

function mergeMultiSelectItemsById(
    ...lists: Array<Array<{id: string; value: string}>>
): Array<{id: string; value: string}> {
    const merged = new Map<string, {id: string; value: string}>();

    for (const list of lists) {
        for (const item of list) {
            merged.set(String(item.id), {
                id: String(item.id),
                value: String(item.value ?? item.id),
            });
        }
    }

    return Array.from(merged.values());
}

function parseMultiSelectItemsJson(raw: string): Array<{id: string; value: string}> {
    const attr = raw.trim();
    if (!attr) {
        return [];
    }

    try {
        const parsed: unknown = JSON.parse(attr);
        if (!Array.isArray(parsed)) {
            return [];
        }

        return parsed
            .map((entry) => normalizeMultiSelectItemEntry(entry))
            .filter((entry): entry is {id: string; value: string} => entry !== null);
    } catch {
        return [];
    }
}

function parseMultiSelectItemsAttribute(element: CbarMultiSelectElement): Array<{id: string; value: string}> {
    return parseMultiSelectItemsJson(element.getAttribute('items') ?? '');
}

function readCachedInitialMultiSelectItems(element: CbarMultiSelectElement): Array<{id: string; value: string}> {
    return parseMultiSelectItemsJson(element.getAttribute(CBAR_MULTI_SELECT_INITIAL_ITEMS_ATTR) ?? '');
}

/** Snapshot server-rendered items before Lit upgrade can shrink the property. */
export function ensureInitialMultiSelectItemsCached(element: CbarMultiSelectElement): void {
    if (element.hasAttribute(CBAR_MULTI_SELECT_ITEMS_MANAGED_ATTR)) {
        return;
    }

    const cached = readCachedInitialMultiSelectItems(element);
    const fromAttribute = parseMultiSelectItemsAttribute(element);
    const merged = mergeMultiSelectItemsById(cached, fromAttribute);

    if (merged.length === 0) {
        return;
    }

    // Never shrink the snapshot — Lit may reflect only selected chips into items.
    if (cached.length > 0 && merged.length <= cached.length) {
        return;
    }

    element.setAttribute(CBAR_MULTI_SELECT_INITIAL_ITEMS_ATTR, JSON.stringify(merged));
}

export function cacheCbarMultiSelectItemsAttributesIn(root: ParentNode = document): void {
    root.querySelectorAll('cbar-multi-select-combo-box, cbar-multi-select').forEach((node) => {
        if (isCbarMultiSelect(node)) {
            ensureInitialMultiSelectItemsCached(node);
        }
    });
}

function normalizeMultiSelectItemsProperty(
    items: Array<{id: string; value: string}> | undefined,
): Array<{id: string; value: string}> {
    if (!Array.isArray(items) || items.length === 0) {
        return [];
    }

    return items.map((item) => ({
        id: String(item.id),
        value: String(item.value ?? ''),
    }));
}

function shouldReplaceMultiSelectItems(
    current: Array<{id: string; value: string}>,
    merged: Array<{id: string; value: string}>,
): boolean {
    if (merged.length === 0) {
        return false;
    }

    if (current.length === 0) {
        return true;
    }

    const currentIds = new Set(current.map((item) => String(item.id)));
    if (merged.length !== current.length) {
        return true;
    }

    return merged.some((item) => !currentIds.has(String(item.id)));
}

/** Reconcile items property with the server-rendered items attribute after Lit upgrade. */
export function syncCbarMultiSelectItemsFromAttribute(element: CbarMultiSelectElement): void {
    if (element.hasAttribute(CBAR_MULTI_SELECT_ITEMS_MANAGED_ATTR)) {
        return;
    }

    ensureInitialMultiSelectItemsCached(element);

    const merged = readInitialMultiSelectItems(element);
    if (merged.length === 0) {
        return;
    }

    const current = normalizeMultiSelectItemsProperty(element.items);
    if (shouldReplaceMultiSelectItems(current, merged)) {
        element.items = merged;
    }
}

function normalizeMultiSelectValueEntry(
    entry: unknown,
    items: Array<{id: string; value: string}>,
): {id: string; value: string} {
    if (typeof entry === 'object' && entry !== null) {
        const record = entry as Record<string, unknown>;
        const id = String(record.id ?? '');
        const value = String(record.value ?? record.title ?? id);
        if (id !== '') {
            const match = items.find((item) => String(item.id) === id);
            return match ?? {id, value};
        }
    }

    const text = String(entry ?? '').trim();
    if (text.startsWith('{')) {
        try {
            const parsed = JSON.parse(text) as Record<string, unknown>;
            if (parsed && typeof parsed === 'object' && parsed.id != null) {
                return normalizeMultiSelectValueEntry(parsed, items);
            }
        } catch {
            // fall through
        }
    }

    const match = items.find((item) => String(item.id) === text || String(item.value) === text);
    return match ?? {id: text, value: text};
}

function readInitialMultiSelectValue(element: CbarMultiSelectElement): Array<{id: string; value: string}> {
    const attr = element.getAttribute('value')?.trim() ?? '';
    if (!attr) {
        return [];
    }

    try {
        const parsed: unknown = JSON.parse(attr);
        if (!Array.isArray(parsed)) {
            return [];
        }

        const items = readInitialMultiSelectItems(element);
        return parsed
            .map((entry) => normalizeMultiSelectValueEntry(entry, items))
            .filter((entry) => entry.id !== '' || entry.value !== '');
    } catch {
        return [];
    }
}

function readInitialMultiSelectItems(element: CbarMultiSelectElement): Array<{id: string; value: string}> {
    const fromCached = readCachedInitialMultiSelectItems(element);
    const fromAttribute = parseMultiSelectItemsAttribute(element);
    const fromProperty = normalizeMultiSelectItemsProperty(element.items);

    // Prefer the server-rendered items list; the property may only contain selected chips.
    return mergeMultiSelectItemsById(fromCached, fromAttribute, fromProperty);
}

/** Mark items as managed by page logic so server-rendered attribute sync is skipped. */
export function markCbarMultiSelectItemsManaged(
    element: CbarMultiSelectElement,
    items: Array<{id: string; value: string}>,
): void {
    element.setAttribute(CBAR_MULTI_SELECT_ITEMS_MANAGED_ATTR, '');
    element.setAttribute('items', JSON.stringify(items));
    element.setAttribute(CBAR_MULTI_SELECT_INITIAL_ITEMS_ATTR, JSON.stringify(items));
}

function filterMultiSelectValueToItems(
    value: Array<{id: string; value: string}>,
    items: Array<{id: string; value: string}>,
): Array<{id: string; value: string}> {
    const itemIds = new Set(items.map((item) => String(item.id)));
    return value.filter((entry) => itemIds.has(String(entry.id)));
}

function clearCbarMultiSelectValueAttribute(element: CbarMultiSelectElement): void {
    element.removeAttribute('value');
}

function wireCbarMultiSelectValueSync(element: CbarMultiSelectElement): void {
    if (element.hasAttribute(CBAR_MULTI_SELECT_VALUE_SYNC_ATTR)) {
        return;
    }

    element.setAttribute(CBAR_MULTI_SELECT_VALUE_SYNC_ATTR, '');
    element.addEventListener('value-changed', () => {
        clearCbarMultiSelectValueAttribute(element);
        syncCbarMultiSelectFormValue(element);
    });
}

function syncCbarMultiSelectFormValue(element: CbarMultiSelectElement): void {
    const name = element.name || element.getAttribute('name') || '';
    if (!name || !element._internals) {
        return;
    }

    const current = Array.isArray(element.value) ? element.value : [];
    if (current.length === 0) {
        element._internals.setFormValue(null);
        return;
    }

    const formData = new FormData();
    getCbarMultiSelectIdsFromElement(element).forEach((id) => {
        formData.append(name, id);
    });
    element._internals.setFormValue(formData);
}

/** Copy server-rendered value="[{id,value}]" into the component property. */
export function hydrateCbarMultiSelect(element: CbarMultiSelectElement): void {
    ensureInitialMultiSelectItemsCached(element);

    if (element.hasAttribute(CBAR_MULTI_SELECT_HYDRATED_ATTR)) {
        syncCbarMultiSelectItemsFromAttribute(element);
        return;
    }

    const items = readInitialMultiSelectItems(element);
    if (shouldReplaceMultiSelectItems(normalizeMultiSelectItemsProperty(element.items), items)) {
        element.items = items;
    }

    const current = Array.isArray(element.value) ? element.value : [];
    if (current.length === 0) {
        const initial = readInitialMultiSelectValue(element);
        if (initial.length > 0) {
            element.value = filterMultiSelectValueToItems(initial, items);
        }
    } else {
        element.value = filterMultiSelectValueToItems(
            current.map((entry) => normalizeMultiSelectValueEntry(entry, items)),
            items,
        );
    }

    clearCbarMultiSelectValueAttribute(element);
    wireCbarMultiSelectValueSync(element);
    syncCbarMultiSelectFormValue(element);
    element.setAttribute(CBAR_MULTI_SELECT_HYDRATED_ATTR, '');
}

export function hydrateCbarMultiSelectsIn(root: ParentNode = document): void {
    root.querySelectorAll('cbar-multi-select-combo-box, cbar-multi-select').forEach((node) => {
        if (isCbarMultiSelect(node)) {
            hydrateCbarMultiSelect(node);
        }
    });
}

/** Stop form-associated multi-selects from also submitting JSON form values. */
function clearCbarMultiSelectFormAssociation(element: CbarMultiSelectElement): void {
    element.removeAttribute('name');
    element.name = '';
    element._internals?.setFormValue(null);
}

function restoreCbarMultiSelectFormAssociation(element: CbarMultiSelectElement, name: string): void {
    element.setAttribute('name', name);
    element.name = name;
    syncCbarMultiSelectFormValue(element);
}

function hiddenInputsForMultiSelectField(
    form: HTMLFormElement,
    name: string,
): HTMLInputElement[] {
    const selector = `input[type="hidden"][${CBAR_MULTI_SELECT_HIDDEN_ATTR}="${escapeSelectorValue(name)}"]`;
    return Array.from(form.querySelectorAll(selector)) as HTMLInputElement[];
}

/**
 * Replace JSON object form values from cbar-multi-select with primitive tag ids
 * before the browser (or FormData) serializes the form.
 */
export function applyCbarMultiSelectSubmitValues(form: HTMLFormElement): void {
    form.querySelectorAll('cbar-multi-select-combo-box[name], cbar-multi-select[name]').forEach((node) => {
        if (!isCbarMultiSelect(node)) {
            return;
        }

        const name = node.getAttribute('name');
        if (!name) {
            return;
        }

        hiddenInputsForMultiSelectField(form, name).forEach((hidden) => hidden.remove());

        const ids = getCbarMultiSelectIdsFromElement(node);
        ids.forEach((id) => {
            const hidden = document.createElement('input');
            hidden.type = 'hidden';
            hidden.name = name;
            hidden.value = id;
            hidden.setAttribute(CBAR_MULTI_SELECT_HIDDEN_ATTR, name);
            form.appendChild(hidden);
        });

        if (!node.hasAttribute(CBAR_MULTI_SELECT_SUBMIT_NAME_ATTR)) {
            node.setAttribute(CBAR_MULTI_SELECT_SUBMIT_NAME_ATTR, name);
            clearCbarMultiSelectFormAssociation(node);
        }
    });
}

export function restoreCbarMultiSelectNames(form: HTMLFormElement): void {
    form.querySelectorAll(
        `cbar-multi-select-combo-box[${CBAR_MULTI_SELECT_SUBMIT_NAME_ATTR}], cbar-multi-select[${CBAR_MULTI_SELECT_SUBMIT_NAME_ATTR}]`,
    ).forEach((node) => {
        const name = node.getAttribute(CBAR_MULTI_SELECT_SUBMIT_NAME_ATTR);
        if (!name) {
            return;
        }

        restoreCbarMultiSelectFormAssociation(node, name);
        node.removeAttribute(CBAR_MULTI_SELECT_SUBMIT_NAME_ATTR);
    });

    form.querySelectorAll(`input[type="hidden"][${CBAR_MULTI_SELECT_HIDDEN_ATTR}]`).forEach((node) => {
        node.parentNode?.removeChild(node);
    });
}

export async function waitForCbarMultiSelectsReady(root: ParentNode = document): Promise<void> {
    cacheCbarMultiSelectItemsAttributesIn(root);
    await customElements.whenDefined('cbar-multi-select-combo-box');
    cacheCbarMultiSelectItemsAttributesIn(root);

    const elements = Array.from(
        root.querySelectorAll('cbar-multi-select-combo-box, cbar-multi-select'),
    ) as LitElementLike[];

    await Promise.all(
        elements.map((element) => element.updateComplete ?? Promise.resolve(true)),
    );

    hydrateCbarMultiSelectsIn(root);

    await Promise.all(
        elements.map((element) => element.updateComplete ?? Promise.resolve(true)),
    );

    elements.forEach((element) => {
        if (isCbarMultiSelect(element)) {
            syncCbarMultiSelectItemsFromAttribute(element);
        }
    });
}

export async function setupCbarMultiSelectHydration(): Promise<void> {
    cacheCbarMultiSelectItemsAttributesIn(document);
    await customElements.whenDefined('cbar-multi-select-combo-box');
    cacheCbarMultiSelectItemsAttributesIn(document);

    const run = () => {
        cacheCbarMultiSelectItemsAttributesIn(document);
        hydrateCbarMultiSelectsIn(document);
    };
    run();

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', run, {once: true});
    }

    const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
            mutation.addedNodes.forEach((node) => {
                if (!(node instanceof HTMLElement)) {
                    return;
                }

                if (isCbarMultiSelect(node)) {
                    ensureInitialMultiSelectItemsCached(node);
                    hydrateCbarMultiSelect(node);
                    return;
                }

                cacheCbarMultiSelectItemsAttributesIn(node);
                hydrateCbarMultiSelectsIn(node);
            });
        }
    });

    observer.observe(document.documentElement, {childList: true, subtree: true});
}
